import { compose, difference, equals, filter, find, findIndex, forEach, head, lensPath, map, mergeLeft, not, nth, pathOr, pipe, propEq, remove, set } from "ramda";
import { META_DATA } from "src/services/constants";
import { isNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";
import { DE_ACTIVATED_UPON, ANSWER, KEY_FIELD, QUESTION_ID } from "../../Intents/intentConstants";
import { ifToAddOrRemoveThisIntentWhenAllPass } from "../Engine/deactivatedUpon";

class SurveyIntent {
  intent: any = {};
  constructor(siteIntent: any, userIntent?: any) {
    const userAnswer = pathOr('', ['answer'], userIntent)
    const metadata = mergeLeft(pathOr({}, [META_DATA], userIntent), pathOr({}, [META_DATA], siteIntent))
    this.intent = compose(
      set(lensPath(['answer']), userAnswer),
      set(lensPath([META_DATA]), metadata),
      set(lensPath(['created_at']), pathOr({}, ['created_at'], userIntent)),
      set(lensPath(['updated_at']), pathOr({}, ['updated_at'], userIntent)),
    )(siteIntent)
  }

  deactivatedUpon() {
    return pathOr([], [DE_ACTIVATED_UPON], this.intent)
  }
  // TODO we have to enable the answer setting with api store data using mkAutoAnswerQuestion
  answer() {
    return pathOr('', [ANSWER], this.intent)
  }

  questionId() {
    return pathOr('', [QUESTION_ID], this.intent)
  }

  pipeWith() {
    return pathOr('', ['pipe_with'], this.intent)
  }

  keyField() {
    return pathOr('', [KEY_FIELD], this.intent)
  }

  short() {
    return {
      [KEY_FIELD]: this.keyField(),
      [QUESTION_ID]: this.questionId(),
      [ANSWER]: this.answer(),
    }
  }
}


class SurveyIntents {
  modifiedSiteIntents: SurveyIntent[] = [];
  userModifiedIntents: SurveyIntent[] = [];
  siteIntents: any = [];
  userSpecificIntent: any = {};
  loanApp?: any;

  constructor(siteIntents: any, userSpecificIntent: any, loanApp?: any) {
    this.siteIntents = siteIntents
    this.loanApp = loanApp
    this.userSpecificIntent = userSpecificIntent
    this.modifiedSiteIntents = map((siteIntent: any) => {
      return new SurveyIntent(
        siteIntent,
        find(
          propEq(QUESTION_ID, pathOr('', ['question_id'], siteIntent)),
          this.userIntents()
        )
      )
    }, siteIntents)

    this.userModifiedIntents = map((userIntent: any) => {
      return new SurveyIntent(
        find(propEq(QUESTION_ID, pathOr('', ['question_id'], userIntent)),
          siteIntents),
        userIntent
      )
    }, this.userIntents())

  }

  userIntents() {
    return pathOr([], ['intents'], this.userSpecificIntent)
  }

  // Site intest which merged with user answer
  userSiteIntents() {
    return this.modifiedSiteIntents
  }

  findUserIntent(intent: SurveyIntent) {
    const index = findIndex(
      propEq('question_id', intent.questionId()),
      this.userIntents()
    );
    return {
      index,
      intent: returnIsNotEmptyOrTrue(not(equals(index, -1)), nth(index, this.userIntents()))
    }
  }

  shortenIntents(intents: SurveyIntent[]) {
    return map((intent: SurveyIntent) => intent.short(), intents)
  }

  nextQuestion() {
    const intentsNotYetRecordedInUserIntents = difference(
      this.modifiedSiteIntents,
      this.userModifiedIntents
    )
    return head(this.filterActivatedIntents(intentsNotYetRecordedInUserIntents))
  }

  activatedUserSpecificIntent() {
    return pipe(
      (activatedQuestion: SurveyIntent[]) =>
        difference(
          this.shortenIntents(this.userModifiedIntents),
          this.shortenIntents(activatedQuestion),
        ),
      (intentsToBeRemoved: any) => this.removeIntents(intentsToBeRemoved)
    )(this.filterActivatedIntents(this.userModifiedIntents));
  }

  removeIntents(intentsToBeRemoved: { question_id: string }[]) {
    let result = this.userIntents();
    const questionIds = map(pathOr("", [QUESTION_ID]), intentsToBeRemoved)

    forEach((questionId: string) => {
      const index = findIndex(
        propEq(QUESTION_ID, questionId),
        this.userIntents()
      );
      if (index >= 0) {
        result = remove(index, 1, result);
      }
    }, questionIds)
    return compose(
      set(lensPath(['intents']), result)
    )(this.userSpecificIntent)
  }

  // modifiedIntents is list of SurveyIntent object with can be SiteIntents or UserIntents
  filterActivatedIntents(
    modifiedIntents: SurveyIntent[]
  ) {

    let result: SurveyIntent[] = modifiedIntents;
    const intentsToBeRemoved = filter((intent: SurveyIntent) => {
      if (isNotEmpty(intent.deactivatedUpon())) {
        // TODO: needs to make class to do this DeActivate decider process
        const thisResult = ifToAddOrRemoveThisIntentWhenAllPass(
          intent.deactivatedUpon(),
          this.userIntents(),
          '',
          intent.pipeWith(),
          [],
          this.loanApp
        );
        return pathOr(false, ['remove'], thisResult);
      }
      return false;
    }, modifiedIntents)

    if (isNotEmpty(intentsToBeRemoved)) {
      const removedQuesion = () => {
        return (
          map((intent: SurveyIntent) => {
            const { index } = this.findUserIntent(intent)
            result = remove(index, 1, result);
            return result;
          }, intentsToBeRemoved)
        );
      };
      removedQuesion();
      return result;
    }

    return result
  }

}

export {
  SurveyIntents,
  SurveyIntent
}
